import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BodyPortal } from '@/components/BodyPortal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Snackbar } from '@/components/Snackbar';
import { useIsMobile } from '@/hooks/useIsMobile';
import { MailAttachmentPreview } from '@/pages/Mail/components/MailAttachmentPreview';
import { VideoCard } from '@/pages/Mail/components/VideoCard';
import { VideoDetailModal } from '@/pages/Mail/components/VideoDetailModal';
import { useJoinVideoMeeting } from '@/pages/Mail/hooks/useJoinVideoMeeting';
import { useRespondToVideoMeeting } from '@/pages/Mail/hooks/useRespondToVideoMeeting';
import { useVideoMeetings } from '@/pages/Mail/hooks/useVideoMeetings';
import { SnackbarVariant } from '@/types/components';
import type { MailAttachment } from '@/types/mail';
import { SectionTitle } from '@/pages/Home/SectionTitle';

export const VideoMeetings: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(
    null,
  );
  const [decliningMeetingId, setDecliningMeetingId] = useState<string | null>(
    null,
  );
  const [previewAttachment, setPreviewAttachment] =
    useState<MailAttachment | null>(null);
  const [isErrorSnackbarOpen, setIsErrorSnackbarOpen] = useState(false);

  const { data: meetings = [] } = useVideoMeetings(true);

  const activeMeetings = meetings.filter(({ isActive }) => isActive);
  const selectedMeeting =
    activeMeetings.find(({ id }) => id === selectedMeetingId) ?? null;

  const { respondingId, approve, decline } = useRespondToVideoMeeting(() =>
    setIsErrorSnackbarOpen(true),
  );
  const joinMeeting = useJoinVideoMeeting({
    meetings: activeMeetings,
    onError: () => setIsErrorSnackbarOpen(true),
  });

  const declineMeeting = () => {
    if (!decliningMeetingId) {
      return;
    }

    decline(decliningMeetingId);
    setDecliningMeetingId(null);
  };

  if (activeMeetings.length === 0) {
    return null;
  }

  return (
    <section className="flex flex-col gap-3 px-4 pt-6 md:gap-4 md:px-6 md:pt-10">
      <SectionTitle>{t('home.videoMeetings.title')}</SectionTitle>

      <div className="flex flex-col gap-4">
        {activeMeetings.map((meeting) => (
          <VideoCard
            key={meeting.id}
            meeting={meeting}
            isMobile={isMobile}
            isResponding={meeting.id === respondingId}
            onOpen={setSelectedMeetingId}
            onJoin={joinMeeting}
            onApprove={approve}
            onDecline={setDecliningMeetingId}
          />
        ))}
      </div>

      {selectedMeeting && (
        <VideoDetailModal
          meeting={selectedMeeting}
          isMobile={isMobile}
          isResponding={selectedMeeting.id === respondingId}
          onClose={() => setSelectedMeetingId(null)}
          onJoin={joinMeeting}
          onApprove={approve}
          onDecline={setDecliningMeetingId}
          onOpenAttachment={setPreviewAttachment}
        />
      )}

      <ConfirmDialog
        isOpen={!!decliningMeetingId}
        title={t('mail.video.detail.declineConfirm.title')}
        message={t('mail.video.detail.declineConfirm.message')}
        confirmLabel={t('mail.video.detail.declineConfirm.confirm')}
        onClose={() => setDecliningMeetingId(null)}
        onConfirm={declineMeeting}
      />

      {previewAttachment && (
        <MailAttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}

      <BodyPortal>
        <Snackbar
          isOpen={isErrorSnackbarOpen}
          variant={SnackbarVariant.Error}
          message={t('global.errorOccurred')}
          onDismiss={() => setIsErrorSnackbarOpen(false)}
        />
      </BodyPortal>
    </section>
  );
};
